/**
 * useTenantFeatures — Fix 2.
 *
 * One-shot fetch of the per-tenant feature manifest from /api/tenant/me/features.
 * Drives:
 *   • Whether the "Responses" sidebar entry shows (has_standard_surveys)
 *   • Whether the "Reports" sidebar entry shows (has_mystery_shopping)
 *   • Whether the Mystery Shopping summary card renders on the dashboard
 *   • Whether the Open Cases / Repeat Issues / Pending Actions widgets render
 *
 * Cached per page-load in module memory so re-mounting subscribers don't
 * re-hit the endpoint. The hook does NOT block render: components receive
 * the safest defaults (everything off) until the fetch resolves.
 */
import { useEffect, useState } from 'react';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';

const DEFAULT_FEATURES = Object.freeze({
  has_standard_surveys: false,
  has_mystery_shopping: false,
  mystery_published_30d: 0,
  mystery_published_total: 0,
  cases_widgets: false,
  // Reduced Phase 1 — MS client dashboard rollout gate (both must be true)
  products_mystery_shopping: false,
  ms_new_dashboard: false,
  // Jun 2026 — product-level feature availability (MS-context today).
  product_features: { management_insights: false, ask_satisfind: false },
  owned_products: [],
  _loaded: false,
});

let _cache = null;
let _inflight = null;

async function _fetchFeatures() {
  if (_cache) return _cache;
  if (!_inflight) {
    _inflight = (async () => {
      try {
        const res = await fetch(`${API}/tenant/me/features`, { credentials: 'include' });
        if (!res.ok) return { ...DEFAULT_FEATURES, _loaded: true };
        const data = await res.json();
        return { ...DEFAULT_FEATURES, ...data, _loaded: true };
      } catch {
        return { ...DEFAULT_FEATURES, _loaded: true };
      }
    })();
  }
  _cache = await _inflight;
  _inflight = null;
  return _cache;
}

/** Reset the in-memory cache. Call on logout or tenant switch. */
export function resetTenantFeatures() {
  _cache = null;
  _inflight = null;
}

export default function useTenantFeatures() {
  const { user } = useAuth();
  const [features, setFeatures] = useState(_cache || DEFAULT_FEATURES);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setFeatures(DEFAULT_FEATURES);
      return () => { cancelled = true; };
    }
    _fetchFeatures().then(f => { if (!cancelled) setFeatures(f); });
    return () => { cancelled = true; };
  }, [user?.email, user?.tenant_id]);

  return features;
}
